import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { DollarSign, TrendingUp, Calendar, ChevronDown, ChevronUp, Target, Award } from "lucide-react";

const brl = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; } };

export const ConsultantFinanceWidget = () => {
  const [data, setData] = useState(null);
  const [range, setRange] = useState(""); // "" = tudo
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const url = range ? `/consultant/me/finance?days=${range}` : "/consultant/me/finance";
      const r = await api.get(url);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao carregar comissões"); }
  }, [range]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-3 sm:p-5 mb-6" data-testid="consultant-finance-widget">
      <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2 sm:gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700" />
          <h2 className="font-display font-bold text-base sm:text-lg text-[#0B2B62]">Minhas comissões</h2>
        </div>
        <div className="flex gap-1 text-[11px] sm:text-xs">
          {[["",""],["30d","30"],["90d","90"],["ano","365"]].map(([lbl,val]) => (
            <button key={lbl||"all"} onClick={() => setRange(val)}
              className={`px-2 sm:px-2.5 py-1 rounded-md border ${range === val ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {lbl || "Tudo"}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
        <Stat label={`Mês (${data.current_month})`} value={brl(data.month_commission)} highlight />
        <Stat label={range ? `${range}d` : "Total"} value={brl(data.total_commission)} icon={<TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />} />
        <Stat label="Confirmados" value={data.total_confirmed} />
      </div>

      {data.monthly_goal ? (
        <GoalBar goal={data.monthly_goal} current={data.month_commission}
                 pct={data.goal_progress_pct} reached={data.goal_reached} />
      ) : (
        <div className="mt-3 text-xs text-slate-500 italic text-center">
          Você ainda não tem meta mensal definida. Peça ao admin para configurá-la no seu perfil.
        </div>
      )}

      {/* Gráfico de barras — histórico 12 meses meta vs. realizado */}
      {data.history_12m?.length > 0 && data.total_commission > 0 && (
        <HistoryChart history={data.history_12m} goal={data.monthly_goal} />
      )}

      {data.items.length === 0 && (!data.booking_items || data.booking_items.length === 0) ? (
        <div className="mt-4 p-4 border border-dashed border-slate-300 rounded-md text-sm text-slate-500 text-center">
          Nenhuma comissão registrada ainda no período.
        </div>
      ) : (
        <>
          <button onClick={() => setExpanded((v) => !v)} data-testid="finance-toggle-history"
            className="mt-4 text-sm text-[#0B2B62] font-semibold inline-flex items-center gap-1 hover:underline">
            {expanded ? <><ChevronUp className="w-4 h-4" /> Fechar histórico</> : <><ChevronDown className="w-4 h-4" /> Ver histórico ({data.items.length} internações + {(data.booking_items || []).length} consultas)</>}
          </button>
          {expanded && (
            <div className="mt-3 space-y-4">
              {data.items.length > 0 && (
                <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="finance-arrivals-block">
                  <div className="px-3 py-2 bg-[#0B2B62] text-white text-[10px] uppercase tracking-widest font-bold">Internações confirmadas ({data.items.length})</div>
                  <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                    <div className="col-span-4">Caso</div>
                    <div className="col-span-3">Unidade</div>
                    <div className="col-span-2">Confirmado</div>
                    <div className="col-span-1 text-right">Mensal</div>
                    <div className="col-span-2 text-right">Comissão</div>
                  </div>
                  {data.items.map((it) => (
                    <div key={it.case_id} data-testid={`finance-row-${it.protocol}`}>
                      <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 border-b border-slate-100 text-xs items-center">
                        <div className="col-span-4">
                          <div className="font-semibold text-[#0B2B62]">{it.protocol}</div>
                          <div className="text-slate-600 truncate">{it.patient_name}</div>
                        </div>
                        <div className="col-span-3 text-slate-700 truncate">{it.clinic_name || "—"}</div>
                        <div className="col-span-2 text-slate-500"><Calendar className="w-3 h-3 inline mr-1" />{fmt(it.confirmed_at)}</div>
                        <div className="col-span-1 text-right text-slate-700 tabular-nums">{brl(it.monthly_value)}</div>
                        <div className="col-span-2 text-right font-bold text-emerald-700 tabular-nums">{brl(it.consultor_commission)}</div>
                      </div>
                      <div className="md:hidden p-3 border-b border-slate-100 text-xs">
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="font-semibold text-[#0B2B62] text-sm">{it.protocol}</div>
                          <div className="font-bold text-emerald-700 tabular-nums text-sm">{brl(it.consultor_commission)}</div>
                        </div>
                        <div className="text-slate-700 truncate mt-0.5">{it.patient_name} · {it.clinic_name || "—"}</div>
                        <div className="mt-1 flex items-center justify-between text-slate-500 text-[10px]">
                          <span><Calendar className="w-3 h-3 inline mr-1" />{fmt(it.confirmed_at)}</span>
                          <span>Mensal <span className="tabular-nums text-slate-700">{brl(it.monthly_value)}</span></span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Iter 68.51 — bloco novo: consultas online (10% da comissão RAF) */}
              {(data.booking_items || []).length > 0 && (
                <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="finance-bookings-block">
                  <div className="px-3 py-2 bg-emerald-700 text-white text-[10px] uppercase tracking-widest font-bold">Consultas online ({data.booking_items.length}) · 10% da comissão RAF</div>
                  <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                    <div className="col-span-3">Data</div>
                    <div className="col-span-3">Profissional</div>
                    <div className="col-span-2">Paciente</div>
                    <div className="col-span-1">Status</div>
                    <div className="col-span-1 text-right">Valor</div>
                    <div className="col-span-2 text-right">Comissão</div>
                  </div>
                  {data.booking_items.map((it) => (
                    <div key={it.booking_id} data-testid={`finance-booking-${it.booking_id}`}>
                      <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 border-b border-slate-100 text-xs items-center">
                        <div className="col-span-3 text-slate-500">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          {new Date(it.scheduled_at).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </div>
                        <div className="col-span-3 font-semibold text-[#0B2B62] truncate">{it.professional_name}</div>
                        <div className="col-span-2 text-slate-700 truncate">{it.family_display}</div>
                        <div className="col-span-1 text-[10px] uppercase tracking-widest text-slate-500">{it.status}</div>
                        <div className="col-span-1 text-right text-slate-700 tabular-nums">{brl(it.session_value)}</div>
                        <div className="col-span-2 text-right font-bold text-emerald-700 tabular-nums">{brl(it.consultant_share)}</div>
                      </div>
                      <div className="md:hidden p-3 border-b border-slate-100 text-xs">
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="font-semibold text-[#0B2B62] text-sm truncate">{it.professional_name}</div>
                          <div className="font-bold text-emerald-700 tabular-nums text-sm">{brl(it.consultant_share)}</div>
                        </div>
                        <div className="text-slate-700 truncate mt-0.5">{it.family_display} · {new Date(it.scheduled_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
                        <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-500">{it.status}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
};

const Stat = ({ label, value, highlight, icon }) => (
  <div className={`p-2 sm:p-3 border rounded-lg ${highlight ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
    <div className={`text-[9px] sm:text-[10px] uppercase tracking-wider sm:tracking-widest font-semibold flex items-center gap-1 truncate ${highlight ? "text-emerald-700" : "text-slate-500"}`}>
      {icon}<span className="truncate">{label}</span>
    </div>
    <div className={`font-display font-bold text-base sm:text-2xl mt-0.5 sm:mt-1 tabular-nums leading-tight ${highlight ? "text-emerald-800" : "text-[#0B2B62]"}`}>{value}</div>
  </div>
);

const GoalBar = ({ goal, current, pct, reached }) => {
  const display = Math.min(pct || 0, 100);
  const remaining = Math.max(0, goal - current);
  return (
    <div className={`mt-3 sm:mt-4 p-3 sm:p-4 border rounded-lg ${reached ? "border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50" : "border-slate-200 bg-white"}`} data-testid="consultant-goal-bar">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          {reached ? <Award className="w-4 h-4 text-amber-600 shrink-0" /> : <Target className="w-4 h-4 text-[#0B2B62] shrink-0" />}
          <span className="text-[10px] sm:text-xs uppercase tracking-wider sm:tracking-widest font-semibold text-slate-600 truncate">
            Meta · {brl(goal)}
          </span>
          {reached && <span className="text-[9px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 rounded-full bg-amber-500 text-white font-bold uppercase tracking-widest">Batida 🎉</span>}
        </div>
        <div className={`font-bold text-base sm:text-lg tabular-nums ${reached ? "text-amber-700" : "text-[#0B2B62]"}`}>{display.toFixed(1)}%</div>
      </div>
      <div className="w-full h-2.5 sm:h-3 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
        <div className={`h-full transition-all duration-500 ${reached ? "bg-gradient-to-r from-amber-400 to-yellow-500" : "bg-gradient-to-r from-emerald-400 to-emerald-600"}`}
             style={{ width: `${display}%` }} data-testid="goal-progress-bar" />
      </div>
      <div className="mt-1.5 text-[10px] sm:text-[11px] text-slate-500 flex justify-between gap-2">
        <span className="truncate">{brl(current)} realizado</span>
        <span className="truncate text-right">{reached ? "Meta batida!" : `Falta ${brl(remaining)}`}</span>
      </div>
    </div>
  );
};

const HistoryChart = ({ history, goal }) => {
  const max = Math.max(goal || 0, ...history.map((h) => h.commission || 0), 1);
  const monthLabel = (m) => {
    const [_, mm] = m.split("-");
    return ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"][Number(mm) - 1] || m;
  };
  return (
    <div className="mt-5 p-4 border border-slate-200 rounded-lg bg-white" data-testid="consultant-history-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest font-semibold text-slate-600">Últimos 12 meses · Meta vs. Realizado</div>
        {goal && (
          <div className="text-[11px] text-slate-500 inline-flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" />Realizado</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-300" />Meta</span>
          </div>
        )}
      </div>
      <div className="flex items-end gap-1 h-32">
        {history.map((h) => {
          const h1 = Math.round((h.commission / max) * 100);
          const gh = goal ? Math.round((goal / max) * 100) : 0;
          return (
            <div key={h.month} className="flex-1 flex flex-col items-center gap-1 group relative"
                 title={`${h.month}: ${brl(h.commission)}${goal ? ` / meta ${brl(goal)}` : ""}`}
                 data-testid={`history-bar-${h.month}`}>
              <div className="w-full h-full flex items-end relative">
                {goal > 0 && (
                  <div className="absolute inset-x-0 border-t-2 border-dashed border-slate-300 pointer-events-none"
                       style={{ bottom: `${gh}%` }} />
                )}
                <div className={`w-full rounded-t ${h.reached ? "bg-gradient-to-t from-amber-400 to-amber-500" : "bg-gradient-to-t from-emerald-400 to-emerald-600"} transition-all`}
                     style={{ height: `${Math.max(h1, 2)}%` }} />
              </div>
              <div className="text-[9px] text-slate-500 uppercase tracking-tight">{monthLabel(h.month)}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 text-[11px] text-slate-500 text-center">
        Total no período: <b className="text-[#0B2B62]">{brl(history.reduce((s,h) => s + h.commission, 0))}</b>
      </div>
    </div>
  );
};

export default ConsultantFinanceWidget;